import * as Api from '@ViewModels';
import FacebookLogoUrl from '../../web/assets/2021_Facebook_icon.svg';
import InstagramLogoUrl from '../../web/assets/Instagram_logo_2022.svg';
import LinkedInLogoUrl from '../../web/assets/LinkedIn_icon.svg';
import { SocialMediaPostStatusDetails } from '../index';

/**
 * Gets social media platform logo URL
 * @source Original location: UiUtils.ts.backup line 315
 */
export const getSocialMediaPlatformLogoUrl = (platform?: Api.SocialMediaType) => {
	let platformLogoUrl = '';

	switch (platform) {
		case Api.SocialMediaType.Facebook:
			platformLogoUrl = FacebookLogoUrl;
			break;
		case Api.SocialMediaType.LinkedIn:
			platformLogoUrl = LinkedInLogoUrl;
			break;
		case Api.SocialMediaType.Instagram:
			platformLogoUrl = InstagramLogoUrl;
			break;
		default:
			break;
	}

	return platformLogoUrl;
};

/**
 * Checks social citation
 * @source Original location: UiUtils.ts.backup line 2259
 */
export const checkSocialCitation = (post: Api.ISocialMediaPost | Api.SocialMediaPostViewModel | Api.ITemplate) => {
	return post?.citation != null && post.citation.length > 0;
};

/**
 * Gets status group options
 * @source Original location: UiUtils.ts.backup line 2444
 */
export const getStatusGroupOptions = (post: Api.SocialMediaPostViewModel) => {
	if (post && [Api.PostStatus.Cancelled, Api.PostStatus.Failed, Api.PostStatus.Unknown].includes(post.status)) {
		return SocialMediaPostStatusDetails.CancelledOrFailed;
	} else if (!post || post.status === Api.PostStatus.Draft) {
		return SocialMediaPostStatusDetails.NewOrDraft;
	} else {
		return SocialMediaPostStatusDetails.NotCancelledFailedDraftOrUnknown;
	}
};

/**
 * Checks if post is scheduled, pending, draft, or new
 * @source Original location: UiUtils.ts.backup line 2454
 */
export const isScheduledOrPendingOrDraftOrNew = (
	post: Api.SocialMediaPostViewModel | Api.SocialMediaPostReportViewModel
) => {
	return !post ||
		post?.status === Api.PostStatus.Scheduled ||
		post?.status === Api.PostStatus.Pending ||
		post?.status === Api.PostStatus.Draft
		? true
		: false;
};

/**
 * Maps social media types to post targets
 * @source Original location: UiUtils.ts.backup line 2465
 */
export const mapSocialMediaTypesToPostTargets = (providerTypes: Api.SocialMediaType[]) => {
	const targets: Api.IPostTarget[] = providerTypes?.map(x => {
		return { provider: x };
	});
	return targets;
};

export const getProviderTypesFromPostTargest = (targets: Api.IPostTarget[]) => {
	const types: Api.SocialMediaType[] = targets?.map(x => {
		return x.provider;
	});
	return types;
};

export const sortPlatformTargets = (platforms: Api.IPostTarget[]) => {
	const sortedPlatforms = platforms
		?.sort((u, v) =>
			u?.state?.lastConnectionState?.postTargetDisplayName === v?.state?.lastConnectionState?.postTargetDisplayName
				? 0
				: u?.state?.lastConnectionState?.postTargetDisplayName > v?.state?.lastConnectionState?.postTargetDisplayName
					? 1
					: -1
		)
		?.sort((c, d) =>
			c?.state?.lastConnectionState?.state === d?.state?.lastConnectionState?.state
				? 0
				: c?.state?.lastConnectionState?.state > d?.state?.lastConnectionState?.state
					? 1
					: -1
		)
		?.sort((a, b) => (a.provider === b.provider ? 0 : a.provider > b.provider ? 1 : -1));
	return sortedPlatforms || [];
};

// A page target must match the same page. LinkedIn shares one userId across a member's profile and
// pages, so only fall back to userId for page-less (profile) targets — matching a page on userId
// would let an orphaned profile drag down a still-live page for the same user.
export const targetMatchesConnection = (target: Api.IPostTarget, connection: Api.ISocialMediaConnection): boolean => {
	if (!target || !connection || target.provider !== connection.type) {
		return false;
	}
	if (target.pageId) {
		return target.pageId === connection.postTargetId;
	}
	return !!target.userId && target.userId === connection.userId;
};

// Drops targets whose social account no longer exists (surfaced by the reconciliation as an
// Unknown-state placeholder connection). Those render no selectable row, so a user can't deselect
// them and a reschedule would re-send them — the post then partially fails on send.
export const removeOrphanedTargets = (
	targets: Api.IPostTarget[],
	connections: Api.ISocialMediaConnection[]
): Api.IPostTarget[] => {
	if (!targets?.length) {
		return targets ?? [];
	}
	const orphanedConnections = connections?.filter(x => x?.state === Api.SocialMediaConnectionState.Unknown) ?? [];
	if (!orphanedConnections.length) {
		return targets;
	}
	return targets.filter(target => !orphanedConnections.some(connection => targetMatchesConnection(target, connection)));
};

export const getRatingIntFromText = (ratingText: string) => {
	const numberMap = {
		ONE: 1,
		TWO: 2,
		THREE: 3,
		FOUR: 4,
		FIVE: 5,
	};
	return numberMap[ratingText as keyof typeof numberMap];
};

export function getContentLength(targetChoice: Api.SocialMediaType) {
	if (targetChoice === Api.SocialMediaType.Instagram) {
		return Api.SocialMediaPreviewLimits.Instagram;
	} else if (targetChoice === Api.SocialMediaType.LinkedIn) {
		return Api.SocialMediaPreviewLimits.LinkedIn;
	}
	return Api.SocialMediaPreviewLimits.FaceBook;
}
